import type { BlogPost } from '#shared/types/Blog'

export type BlogSearchOptions = {
    tags?: string[]
    authorId?: string
    isFeatured?: boolean
    hitsPerPage?: number
    page?: number
}

export type BlogSearchResult = {
    hits: BlogPost[]
    found: number
    page: number
}

function normalizeBlogSearchResult(response: unknown, fallbackPage: number): BlogSearchResult {
    if (!response || typeof response !== 'object') {
        return { hits: [], found: 0, page: fallbackPage }
    }

    const payload = response as {
        hits?: unknown
        found?: unknown
        page?: unknown
    }

    const hits = Array.isArray(payload.hits) ? (payload.hits as BlogPost[]) : []
    const found = typeof payload.found === 'number' ? payload.found : 0
    const page = typeof payload.page === 'number' ? payload.page : fallbackPage

    return { hits, found, page }
}

/**
 * Composable for blog search functionality using Typesense
 */
export function useBlogSearch() {
    /**
     * Search blog posts using Typesense semantic search
     */
    async function semanticSearch(query: string, options: BlogSearchOptions = {}): Promise<BlogSearchResult> {
        const requestedPage = options.page ?? 1

        try {
            const response = await $fetch<unknown>('/api/blogs/search', {
                method: 'POST',
                body: {
                    query,
                    options
                }
            })

            return normalizeBlogSearchResult(response, requestedPage)
        } catch (error) {
            console.error('Error performing semantic search:', error)
            return { hits: [], found: 0, page: requestedPage }
        }
    }

    /**
     * Search blog posts using keyword search
     */
    async function keywordSearch(query: string, options: BlogSearchOptions = {}): Promise<BlogSearchResult> {
        const requestedPage = options.page ?? 1

        try {
            const response = await $fetch<unknown>('/api/blogs/search', {
                method: 'POST',
                body: {
                    query,
                    searchType: 'keyword',
                    options
                }
            })

            return normalizeBlogSearchResult(response, requestedPage)
        } catch (error) {
            console.error('Error performing keyword search:', error)
            return { hits: [], found: 0, page: requestedPage }
        }
    }

    /**
     * Search blog posts with autocomplete
     */
    async function autocompleteSearch(query: string): Promise<Array<{ title: string; slug: string; excerpt: string }>> {
        try {
            if (!query || query.length < 2) return []

            const { hits } = await semanticSearch(query, { hitsPerPage: 5 })

            return hits.map((post) => ({
                title: post.title,
                slug: post.slug,
                excerpt: post.excerpt
            }))
        } catch (error) {
            console.error('Error performing autocomplete search:', error)
            return []
        }
    }

    /**
     * Get all unique tags from published posts
     */
    async function getAllTags(): Promise<string[]> {
        try {
            const tags = await $fetch<string[]>('/api/blogs/tags')
            return Array.isArray(tags) ? tags : []
        } catch (error) {
            console.error('Error getting all tags:', error)
            return []
        }
    }

    /**
     * Get related posts based on tags and content similarity
     */
    async function getRelatedPosts(post: BlogPost, limitCount: number = 3): Promise<BlogPost[]> {
        try {
            // Use semantic search with post content for similarity
            const searchQuery = `${post.title} ${post.tags?.join(' ') || ''}`

            const { hits } = await semanticSearch(searchQuery, {
                hitsPerPage: limitCount + 1 // +1 to account for current post
            })

            // Filter out the current post
            return hits.filter((p) => p.id !== post.id).slice(0, limitCount)
        } catch (error) {
            console.error('Error getting related posts:', error)
            return []
        }
    }

    return {
        semanticSearch,
        keywordSearch,
        autocompleteSearch,
        getAllTags,
        getRelatedPosts
    }
}
